import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getProjects,
  getProject,
  updateProject,
  linkSpace,
  unlinkSpace,
  addProjectMember,
  removeProjectMember,
  addProjectGroup,
  removeProjectGroup,
  getProjectReportSummary,
  getProjectTasks,
  getProjectMileage,
  getProjectTime,
} from '../services/projectService';
import { getClients } from '../services/clientService';
import { listUsers } from '../services/userService';
import { useSpaces } from '../context/SpaceContext';
import './ProjectDetailPage.css';

const ROLE_OPTIONS = ['owner', 'admin', 'member', 'viewer', 'accounting'];

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { spaces = [] } = useSpaces() || {};

  const [projects, setProjects] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tab, setTab] = useState('overview');
  const [clients, setClients] = useState([]);

  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [mileageLogs, setMileageLogs] = useState([]);
  const [mileageLoading, setMileageLoading] = useState(false);
  const [totalKm, setTotalKm] = useState(0);
  const [timeLogs, setTimeLogs] = useState([]);
  const [timeLoading, setTimeLoading] = useState(false);
  const [totalHours, setTotalHours] = useState(0);

  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const [spaceIdInput, setSpaceIdInput] = useState('');
  const [linkingSpace, setLinkingSpace] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [memberUserId, setMemberUserId] = useState('');
  const [memberRole, setMemberRole] = useState('member');
  const [groupId, setGroupId] = useState('');
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');

  const isDetailView = Boolean(projectId);

  useEffect(() => {
    listUsers()
      .then((res) => setAllUsers(res.data.users || []))
      .catch((err) => console.error('Failed to load users:', err));
  }, []);

  const loadSummary = async (id) => {
    setSummaryLoading(true);
    try {
      const res = await getProjectReportSummary(id);
      setSummary(res.data.summary || null);
      setSummaryError('');
    } catch (err) {
      setSummary(null);
      setSummaryError(err?.response?.data?.message || 'No reporting access for this project summary.');
    } finally {
      setSummaryLoading(false);
    }
  };

  const refreshProject = async (id) => {
    const res = await getProject(id);
    const p = res.data.project;
    setSelectedProject(p);
    setEditData({ ...p, client: p.client?._id || p.client || '' });
    return p;
  };

  useEffect(() => {
    setListLoading(true);
    Promise.all([getProjects(), getClients()])
      .then(([projRes, clientRes]) => {
        setProjects(projRes.data.projects || []);
        setClients(clientRes.data.clients || []);
      })
      .catch((err) => console.error('Failed to load projects:', err))
      .finally(() => setListLoading(false));
  }, []);

  useEffect(() => {
    if (!projectId) return;
    refreshProject(projectId)
      .then(() => loadSummary(projectId))
      .catch((err) => console.error('Failed to load project:', err));
  }, [projectId]);

  useEffect(() => {
    if (!selectedProject || tab !== 'tasks') return;
    setTasksLoading(true);
    getProjectTasks(selectedProject._id)
      .then((res) => setTasks(res.data.tasks || []))
      .catch((err) => console.error('Failed to load tasks:', err))
      .finally(() => setTasksLoading(false));
  }, [selectedProject, tab]);

  useEffect(() => {
    if (!selectedProject || tab !== 'mileage') return;
    setMileageLoading(true);
    getProjectMileage(selectedProject._id)
      .then((res) => {
        setMileageLogs(res.data.logs || []);
        setTotalKm(res.data.totalKm || 0);
      })
      .catch((err) => console.error('Failed to load mileage:', err))
      .finally(() => setMileageLoading(false));
  }, [selectedProject, tab]);

  useEffect(() => {
    if (!selectedProject || tab !== 'time') return;
    setTimeLoading(true);
    getProjectTime(selectedProject._id)
      .then((res) => {
        const logs = res.data.logs || [];
        const totalMinutes = res.data.totalMinutes || 0;
        setTimeLogs(logs);
        setTotalHours(totalMinutes / 60);
      })
      .catch((err) => console.error('Failed to load time logs:', err))
      .finally(() => setTimeLoading(false));
  }, [selectedProject, tab]);

  useEffect(() => {
    if (!selectedProject || tab !== 'reports') return;
    loadSummary(selectedProject._id);
  }, [selectedProject, tab]);

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;
    setSaving(true);
    try {
      const res = await updateProject(selectedProject._id, {
        ...editData,
        client: editData.client || null,
      });
      const updated = res.data.project;
      setSelectedProject(updated);
      setProjects((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
      await loadSummary(updated._id);
      setEditing(false);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const handleLinkSpace = async (e) => {
    e.preventDefault();
    if (!spaceIdInput.trim() || !selectedProject) return;
    setLinkingSpace(true);
    try {
      const res = await linkSpace(selectedProject._id, spaceIdInput.trim());
      setSelectedProject((prev) => ({ ...prev, spaces: res.data.project.spaces || [] }));
      setSpaceIdInput('');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to link space');
    } finally {
      setLinkingSpace(false);
    }
  };

  const handleUnlinkSpace = async (spaceId) => {
    if (!window.confirm('Remove this space from the project?')) return;
    try {
      const res = await unlinkSpace(selectedProject._id, spaceId);
      setSelectedProject((prev) => ({ ...prev, spaces: res.data.project.spaces || [] }));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to unlink space');
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedProject || !memberUserId) return;
    try {
      await addProjectMember(selectedProject._id, memberUserId, memberRole);
      setMemberUserId('');
      setMemberRole('member');
      await refreshProject(selectedProject._id);
      await loadSummary(selectedProject._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to add project member');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedProject) return;
    try {
      await removeProjectMember(selectedProject._id, userId);
      await refreshProject(selectedProject._id);
      await loadSummary(selectedProject._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to remove project member');
    }
  };

  const handleAddGroup = async (e) => {
    e.preventDefault();
    if (!selectedProject || !groupId) return;
    try {
      await addProjectGroup(selectedProject._id, groupId);
      setGroupId('');
      await refreshProject(selectedProject._id);
      await loadSummary(selectedProject._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to add project group');
    }
  };

  const handleRemoveGroup = async (id) => {
    if (!selectedProject) return;
    try {
      await removeProjectGroup(selectedProject._id, id);
      await refreshProject(selectedProject._id);
      await loadSummary(selectedProject._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to remove project group');
    }
  };

  const openProject = (p) => {
    navigate(`/projects/${p._id}`);
  };

  if (!isDetailView) {
    return (
      <div className="project-list-page">
        <div className="project-list-header">
          <h1 className="project-list-title">Projects</h1>
          <Link to="/clients" className="btn btn-ghost btn-sm">+ New Project (via Clients)</Link>
        </div>
        {listLoading ? (
          <p className="panel-muted">Loading projects...</p>
        ) : projects.length === 0 ? (
          <p className="panel-muted">No projects yet. Create one from the Clients page.</p>
        ) : (
          <ul className="project-list">
            {projects.map((p) => (
              <li key={p._id} className="project-list-item" onClick={() => openProject(p)}>
                <div className="project-list-info">
                  <span className="project-list-name">{p.name}</span>
                  {p.client && (
                    <span className="project-list-client">
                      {clients.find((c) => c._id === (p.client?._id || p.client))?.name || 'Client'}
                    </span>
                  )}
                  {p.description && <span className="project-list-desc">{p.description}</span>}
                </div>
                <span className={`project-status-badge status-${p.status}`}>{p.status}</span>
                <span className="project-item-arrow">{String.fromCharCode(8250)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (!selectedProject) {
    return <div className="project-detail-page"><p className="panel-muted">Loading...</p></div>;
  }

  return (
    <div className="project-detail-page">
      <div className="project-detail-header">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/projects')}>{'<-'} Projects</button>
        <div className="project-detail-title-area">
          <h1 className="project-detail-name">{selectedProject.name}</h1>
          {selectedProject.client && (
            <Link
              className="project-detail-client-link"
              to={`/clients/${selectedProject.client?._id || selectedProject.client}`}
            >
              Client: {clients.find((c) => c._id === (selectedProject.client?._id || selectedProject.client))?.name || 'Client'}
            </Link>
          )}
        </div>
        <span className={`project-status-badge status-${selectedProject.status}`}>{selectedProject.status}</span>
      </div>

      <div className="project-tabs">
        {['overview', 'tasks', 'mileage', 'time', 'reports'].map((t) => (
          <button
            key={t}
            className={`project-tab-btn ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="project-overview">
          {editing ? (
            <form onSubmit={handleUpdateProject} className="project-edit-form">
              <div className="form-group">
                <label className="form-label">Project Name *</label>
                <input className="form-input" value={editData.name || ''} onChange={(e) => setEditData((p) => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" rows={3} value={editData.description || ''} onChange={(e) => setEditData((p) => ({ ...p, description: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={editData.status || 'active'} onChange={(e) => setEditData((p) => ({ ...p, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="on-hold">On Hold</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Visibility</label>
                <select className="form-select" value={editData.visibility || 'private'} onChange={(e) => setEditData((p) => ({ ...p, visibility: e.target.value }))}>
                  <option value="private">Private</option>
                  <option value="shared">Shared</option>
                  <option value="org">Org-wide</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Client</label>
                <select className="form-select" value={editData.client || ''} onChange={(e) => setEditData((p) => ({ ...p, client: e.target.value }))}>
                  <option value="">No client</option>
                  {clients.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input type="date" className="form-input" value={editData.startDate ? new Date(editData.startDate).toISOString().split('T')[0] : ''} onChange={(e) => setEditData((p) => ({ ...p, startDate: e.target.value || null }))} />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input type="date" className="form-input" value={editData.endDate ? new Date(editData.endDate).toISOString().split('T')[0] : ''} onChange={(e) => setEditData((p) => ({ ...p, endDate: e.target.value || null }))} />
              </div>
              <div className="form-row">
                <button type="submit" className="btn btn-primary" disabled={saving || !editData.name?.trim()}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
              </div>
            </form>
          ) : (
            <div className="project-info-view">
              <button className="btn btn-ghost btn-sm" style={{ marginBottom: 16 }} onClick={() => { setEditing(true); setEditData({ ...selectedProject, client: selectedProject.client?._id || selectedProject.client || '' }); }}>Edit</button>
              <div className="project-info-grid">
                <div className="project-info-item">
                  <span className="project-info-label">Status</span>
                  <span className={`project-status-badge status-${selectedProject.status}`}>{selectedProject.status}</span>
                </div>
                <div className="project-info-item">
                  <span className="project-info-label">Visibility</span>
                  <span className="project-info-value">{selectedProject.visibility || 'private'}</span>
                </div>
                {selectedProject.startDate && (
                  <div className="project-info-item">
                    <span className="project-info-label">Start</span>
                    <span className="project-info-value">{new Date(selectedProject.startDate).toLocaleDateString()}</span>
                  </div>
                )}
                {selectedProject.endDate && (
                  <div className="project-info-item">
                    <span className="project-info-label">End</span>
                    <span className="project-info-value">{new Date(selectedProject.endDate).toLocaleDateString()}</span>
                  </div>
                )}
                {selectedProject.description && (
                  <div className="project-info-item project-info-full">
                    <span className="project-info-label">Description</span>
                    <span className="project-info-value">{selectedProject.description}</span>
                  </div>
                )}
              </div>

              <div className="project-spaces-section">
                <h3 className="project-section-heading">Linked Spaces</h3>
                {(selectedProject.spaces || []).length === 0 ? (
                  <p className="panel-muted">No spaces linked.</p>
                ) : (
                  <ul className="project-spaces-list">
                    {(selectedProject.spaces || []).map((s) => {
                      const sid = s._id || s;
                      const name = s.name || sid;
                      return (
                        <li key={String(sid)} className="project-space-item">
                          <Link to={`/spaces/${sid}`} className="project-space-name">#{name}</Link>
                          <button className="btn-icon-danger" title="Unlink" onClick={() => handleUnlinkSpace(sid)}>X</button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <form onSubmit={handleLinkSpace} className="project-link-form">
                  <input
                    className="form-input form-input-sm"
                    placeholder="Space ID to link..."
                    value={spaceIdInput}
                    onChange={(e) => setSpaceIdInput(e.target.value)}
                  />
                  <button type="submit" className="btn btn-ghost btn-sm" disabled={linkingSpace || !spaceIdInput.trim()}>
                    {linkingSpace ? 'Linking...' : 'Link Space'}
                  </button>
                </form>
              </div>

              <div className="project-spaces-section">
                <h3 className="project-section-heading">Access Control</h3>
                <div className="form-row" style={{ alignItems: 'flex-start' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Members</label>
                    {(selectedProject.members || []).length === 0 ? (
                      <p className="panel-muted">No direct members.</p>
                    ) : (
                      <ul className="project-spaces-list">
                        {(selectedProject.members || []).map((m) => (
                          <li key={String(m.user?._id || m.user)} className="project-space-item">
                            <span className="project-space-name">{m.user?.name || m.user} ({m.role})</span>
                            <button className="btn-icon-danger" onClick={() => handleRemoveMember(m.user?._id || m.user)} title="Remove">X</button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <form onSubmit={handleAddMember} className="form-row" style={{ marginTop: 8 }}>
                      <select className="form-select" value={memberUserId} onChange={(e) => setMemberUserId(e.target.value)}>
                        <option value="">Select user</option>
                        {allUsers.map((u) => (
                          <option key={u._id} value={u._id}>{u.name}</option>
                        ))}
                      </select>
                      <select className="form-select" value={memberRole} onChange={(e) => setMemberRole(e.target.value)}>
                        {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <button type="submit" className="btn btn-ghost btn-sm" disabled={!memberUserId}>Add</button>
                    </form>
                  </div>

                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Groups (Spaces)</label>
                    {(selectedProject.groupIds || []).length === 0 ? (
                      <p className="panel-muted">No linked groups.</p>
                    ) : (
                      <ul className="project-spaces-list">
                        {(selectedProject.groupIds || []).map((g) => (
                          <li key={String(g._id || g)} className="project-space-item">
                            <span className="project-space-name">{g.name || g}</span>
                            <button className="btn-icon-danger" onClick={() => handleRemoveGroup(g._id || g)} title="Remove">X</button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <form onSubmit={handleAddGroup} className="form-row" style={{ marginTop: 8 }}>
                      <select className="form-select" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                        <option value="">Select group</option>
                        {spaces.map((s) => (
                          <option key={s._id} value={s._id}>{s.name}</option>
                        ))}
                      </select>
                      <button type="submit" className="btn btn-ghost btn-sm" disabled={!groupId}>Add</button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'tasks' && (
        <div className="project-tasks-tab">
          {tasksLoading ? (
            <p className="panel-muted">Loading tasks...</p>
          ) : tasks.length === 0 ? (
            <p className="panel-muted">No tasks linked to this project.</p>
          ) : (
            <ul className="project-task-list">
              {tasks.map((t) => (
                <li key={t._id} className="project-task-item">
                  <span className={`kanban-dot dot-${t.status}`} />
                  <div className="project-task-info">
                    <span className="project-task-title">{t.title}</span>
                    {t.assignedTo && <span className="project-task-assignee">User: {t.assignedTo.name}</span>}
                  </div>
                  <span className={`task-priority-chip priority-${t.priority}`}>{t.priority}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === 'mileage' && (
        <div className="project-mileage-tab">
          <div className="project-mileage-summary">
            <span className="mileage-total-label">Total km (project):</span>
            <span className="mileage-total-value">{totalKm.toFixed(1)} km</span>
          </div>
          {mileageLoading ? (
            <p className="panel-muted">Loading mileage...</p>
          ) : mileageLogs.length === 0 ? (
            <p className="panel-muted">No mileage logs for this project.</p>
          ) : (
            <table className="mileage-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Driver</th>
                  <th>Purpose</th>
                  <th>From -> To</th>
                  <th>km</th>
                  <th>Vehicle</th>
                </tr>
              </thead>
              <tbody>
                {mileageLogs.map((log) => (
                  <tr key={log._id}>
                    <td data-label="Date">{new Date(log.date).toLocaleDateString()}</td>
                    <td data-label="Driver">{log.userId?.name || '-'}</td>
                    <td data-label="Purpose">{log.purpose}</td>
                    <td data-label="From -> To">{[log.startLocation, log.endLocation].filter(Boolean).join(' -> ') || '-'}</td>
                    <td data-label="KM">{log.kmDriven?.toFixed(1)}</td>
                    <td data-label="Vehicle">{log.vehicle || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'time' && (
        <div className="project-time-tab">
          <div className="project-time-summary">
            <span className="time-total-label">Total hours (project):</span>
            <span className="time-total-value">{totalHours.toFixed(2)} hrs</span>
          </div>
          {timeLoading ? (
            <p className="panel-muted">Loading time logs...</p>
          ) : timeLogs.length === 0 ? (
            <p className="panel-muted">No time logs for this project yet.</p>
          ) : (
            <table className="time-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Employee</th>
                  <th>Hours</th>
                  <th>Billable</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {timeLogs.map((log) => (
                  <tr key={log._id}>
                    <td data-label="Date">{new Date(log.date).toLocaleDateString()}</td>
                    <td data-label="Employee">{log.userId?.name || '-'}</td>
                    <td data-label="Hours">{((log.minutes || 0) / 60).toFixed(2)}</td>
                    <td data-label="Billable">{log.billable ? 'Yes' : 'No'}</td>
                    <td data-label="Description">{log.description || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'reports' && (
        <div className="project-time-tab">
          <h3 className="project-section-heading">Accounting Summary</h3>
          {summaryLoading && <p className="panel-muted">Loading report summary...</p>}
          {!summaryLoading && summaryError && <p className="panel-muted">{summaryError}</p>}
          {!summaryLoading && !summary && !summaryError && <p className="panel-muted">No report data yet.</p>}
          {!summaryLoading && summary && (
            <div>
              <div className="project-time-summary">
                <span className="time-total-label">Total hours:</span>
                <span className="time-total-value">{(summary.totals?.hours || 0).toFixed(2)}</span>
              </div>
              <div className="project-mileage-summary">
                <span className="mileage-total-label">Total billable hours:</span>
                <span className="mileage-total-value">{(summary.totals?.billableHours || 0).toFixed(2)}</span>
              </div>
              <div className="project-mileage-summary">
                <span className="mileage-total-label">Total km:</span>
                <span className="mileage-total-value">{(summary.totals?.km || 0).toFixed(1)}</span>
              </div>
              <table className="time-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Hours</th>
                    <th>Billable</th>
                    <th>KM</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary.users || []).map((row) => (
                    <tr key={row.userId}>
                      <td>{row.userName}</td>
                      <td>{(row.totalHours || 0).toFixed(2)}</td>
                      <td>{(row.billableHours || 0).toFixed(2)}</td>
                      <td>{(row.totalKm || 0).toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
